import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

// ─────────────────────────────────────────────────────────────────────────────
//  PaywallController — global, contextual paywall sheet.
//
//  Any code path can pop the paywall by calling:
//      const { openPaywall } = usePaywall();
//      openPaywall({ featureKey: "photos.smart_albums" });
//
//  The 402 axios interceptor in /lib/api.js also calls this automatically when
//  the backend raises `PaywallRequired`. Consumers never need a local `paywall`
//  boolean state anymore.
//
//  Shape of the open payload:
//      featureKey         str  — what the user just tried to do
//      requiredTier       str? — backend-provided (preferred) or computed
//      requiredTierLabel  str? — friendly label e.g. "Lite"
//      currentTier        str? — current tier of caller
//      message            str? — backend's pre-baked upgrade pitch (overrides default)
// ─────────────────────────────────────────────────────────────────────────────

const Ctx = createContext(null);

export function PaywallProvider({ children }) {
  const [payload, setPayload] = useState(null);

  const openPaywall = useCallback((p) => {
    setPayload(p || {});
  }, []);

  const closePaywall = useCallback(() => {
    setPayload(null);
  }, []);

  const value = useMemo(() => ({
    payload,
    isOpen: !!payload,
    openPaywall,
    closePaywall,
  }), [payload, openPaywall, closePaywall]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function usePaywall() {
  const ctx = useContext(Ctx);
  if (!ctx) {
    // Safe no-op fallback so components don't crash before provider mounts
    // (e.g. during the auth/onboarding gate).
    return { isOpen: false, payload: null, openPaywall: () => {}, closePaywall: () => {} };
  }
  return ctx;
}

// Module-level singleton so the axios interceptor (non-React code) can
// trigger the paywall. Set once when PaywallProvider mounts via the
// useEffect in <PaywallBridge />.
let _openPaywallRef = (_p) => { /* no-op until bridge mounts */ };

export function getGlobalPaywallOpener() {
  return _openPaywallRef;
}

export function setGlobalPaywallOpener(fn) {
  _openPaywallRef = fn || ((_p) => {});
}
